/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { RadioButtonModule } from "primeng/radiobutton";
import { TooltipModule } from "primeng/tooltip";
import { MessageModule } from "primeng/message";
import { InputTextarea } from "primeng/inputtextarea";
import { MessageService } from "primeng/api";
import { SkeletonModule } from "primeng/skeleton";
import { Subject, takeUntil } from "rxjs";

import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import {
  ProcessingCycleVoidRequest,
  ProcessingCycleVoidType,
} from "../../interfaces/processing-cycle.interface";
import { Site } from "../../../../core/models/site.interface";
import { ScheduledOrderListItem } from "../../interfaces/scheduled-orders-list-item.interface";

/**
 * Processing Cycle Void Dialog
 * Allows voiding a processing cycle
 * Matches old QMC VoidCyclePopup functionality
 *
 * Supports dual mode:
 * - Standard mode: Uses SessionService for authentication
 * - Embedded mode: Uses inputToken passed via config.data
 */
@Component({
  selector: "app-processing-cycle-void-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RadioButtonModule,
    InputTextarea,
    SkeletonModule,
    TooltipModule,
    MessageModule,
  ],
  template: `
    <div class="void-dialog">
      <!-- Loading State -->
      <div *ngIf="isLoadingOccurrences" class="loading-state">
        <p-skeleton width="100%" height="3rem" styleClass="mb-3" />
        <p-skeleton width="100%" height="3rem" styleClass="mb-3" />
        <p-skeleton width="100%" height="5rem" />
      </div>

      <!-- Form -->
      <div *ngIf="!isLoadingOccurrences" class="dialog-content">
        <!-- Instructions -->
        <p class="instructions">
          Please choose from one of the following actions to void the Processing
          Cycle
        </p>

        <!-- Void Type Options (Radio Buttons) -->
        <div class="void-type-options">
          <div
            *ngFor="let option of voidTypeOptions"
            class="radio-option"
            [class.disabled]="option.disabled"
          >
            <p-radioButton
              [inputId]="'void-' + option.value"
              [value]="option.value"
              [(ngModel)]="voidType"
              [disabled]="!!option.disabled"
            />
            <label
              [for]="'void-' + option.value"
              class="radio-label"
              [class.disabled]="option.disabled"
              [pTooltip]="
                option.disabled
                  ? 'Cannot void and skip the Scheduled Order to the next cycle because the Next Occurrence was manually changed after processing'
                  : ''
              "
              tooltipPosition="right"
            >
              {{ option.label }}
              <strong
                *ngIf="
                  option.value === voidTypeEnum.VoidAndSkip &&
                  skippedNextOccurrence &&
                  !option.disabled
                "
                class="occurrence-display"
              >
                (Next Occurrence: {{ formatDate(skippedNextOccurrence) }})
              </strong>
              <strong
                *ngIf="
                  option.value === voidTypeEnum.VoidAndProcessNow &&
                  nextOccurrence
                "
                class="occurrence-display"
              >
                (Next Occurrence: {{ formatDate(nextOccurrence) }})
              </strong>
            </label>
          </div>
        </div>

        <!-- Notes -->
        <div class="field">
          <label for="notes" class="block mb-2 font-semibold">
            Notes <span class="text-red-500">*</span>
          </label>
          <textarea
            id="notes"
            pInputTextarea
            [(ngModel)]="notes"
            rows="5"
            class="w-full"
            placeholder="Please add a note specifying the reason for voiding this processing cycle"
          ></textarea>
        </div>

        <!-- Warning Message -->
        <p-message
          severity="warn"
          styleClass="w-full"
          [text]="
            'Voiding this processing cycle will update Scheduled Order #' +
            (scheduledOrder?.Id || '') +
            '. Calculated totals including \\'Cycles\\' and \\'Lifetime Value\\' will be updated.'
          "
        />
      </div>

      <!-- Footer -->
      <div class="dialog-footer">
        <button
          pButton
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (click)="onCancel()"
          [disabled]="isLoading"
        ></button>
        <button
          pButton
          label="Void Cycle"
          severity="danger"
          (click)="onVoid()"
          [loading]="isLoading"
          [disabled]="voidType === null || !notes"
        ></button>
      </div>
    </div>
  `,
  styles: [
    `
      .void-dialog {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      .loading-state {
        padding: 1rem 0;
      }

      .dialog-content {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }

      .instructions {
        margin: 0;
        color: var(--text-color-secondary);
        font-size: 0.95rem;
      }

      .void-type-options {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 0.5rem;
        border: 1px solid var(--surface-border);
        border-radius: 6px;
        background-color: var(--surface-50);
      }

      .radio-option {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 0.5rem;

        &.disabled {
          opacity: 0.6;
        }
      }

      .radio-label {
        flex: 1;
        cursor: pointer;
        user-select: none;
        line-height: 1.5;

        &.disabled {
          cursor: not-allowed;
          color: var(--text-color-secondary);
        }

        .occurrence-display {
          display: block;
          margin-top: 0.25rem;
          color: var(--primary-color);
          font-weight: 600;
          font-size: 0.9rem;
        }
      }

      .field {
        display: flex;
        flex-direction: column;
      }

      .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding-top: 1rem;
        border-top: 1px solid var(--surface-border);
      }

      .text-red-500 {
        color: var(--red-500);
      }
    `,
  ],
})
export class ProcessingCycleVoidDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  site: Site | null = null;
  scheduledOrder: ScheduledOrderListItem | null = null;
  nextOccurrence: Date | null = null;
  skippedNextOccurrence: Date | null = null;
  nextOccurrenceChanged: boolean = false;
  voidType: ProcessingCycleVoidType | null = null;
  notes: string = "";
  inputToken?: QPilotAccessToken;
  isLoading = false;
  isLoadingOccurrences = false;

  // Expose enum to template
  voidTypeEnum = ProcessingCycleVoidType;

  voidTypeOptions = [
    {
      label: "Void and Pause the Scheduled Order",
      value: ProcessingCycleVoidType.VoidAndPause,
    },
    {
      label: "Void and Skip to the next cycle",
      value: ProcessingCycleVoidType.VoidAndSkip,
      disabled: false, // Will be set dynamically based on nextOccurrenceChanged
    },
    {
      label: "Void and Process the Scheduled Order",
      value: ProcessingCycleVoidType.VoidAndProcessNow,
    },
    {
      label: "Void and Activate",
      value: ProcessingCycleVoidType.VoidAndActivate,
    },
    {
      label: "Void and Leave as is",
      value: ProcessingCycleVoidType.VoidAndLeaveAsIs,
    },
  ];

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private timezoneHelper: TimezoneHelper,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Get data from config
    this.site = this.config.data?.site;
    this.scheduledOrder = this.config.data?.scheduledOrder;
    this.nextOccurrenceChanged =
      this.config.data?.nextOccurrenceChanged ?? false;
    this.notes = this.config.data?.notes ?? "";
    this.inputToken = this.config.data?.inputToken;

    if (this.scheduledOrder) {
      this.nextOccurrence = this.scheduledOrder.NextOccurrenceUtc
        ? new Date(this.scheduledOrder.NextOccurrenceUtc)
        : null;
    }

    // Disable VoidAndSkip option if next occurrence was manually changed (matches old QMC)
    if (this.nextOccurrenceChanged) {
      const skipOption = this.voidTypeOptions.find(
        (opt) => opt.value === ProcessingCycleVoidType.VoidAndSkip,
      );
      if (skipOption) {
        skipOption.disabled = true;
      }
    }

    this.loadSkippedOccurrence();
  }

  /**
   * Get access token based on mode
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Load skipped next occurrence
   */
  private loadSkippedOccurrence(): void {
    const accessToken = this.getAccessToken();
    if (
      !this.scheduledOrder ||
      !this.nextOccurrence ||
      !this.site ||
      !accessToken
    ) {
      return;
    }

    this.isLoadingOccurrences = true;

    this.qpilotService
      .getScheduledOrderNextOccurrenceFromDate(
        this.scheduledOrder,
        this.nextOccurrence,
        this.site.Id,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (nextOccurrence) => {
          this.skippedNextOccurrence = new Date(nextOccurrence);
          this.isLoadingOccurrences = false;
        },
        error: (error) => {
          console.error("Failed to load skipped occurrence:", error);
          this.isLoadingOccurrences = false;
        },
      });
  }

  /**
   * Format date with timezone
   */
  formatDate(date: Date | null): string {
    if (!date) return "-";
    return this.timezoneHelper.formatDateWithTimezone(
      date,
      this.site?.UtcOffset ?? 0,
    );
  }

  /**
   * Cancel dialog
   */
  onCancel(): void {
    this.ref.close();
  }

  /**
   * Void cycle
   */
  onVoid(): void {
    const accessToken = this.getAccessToken();
    if (
      this.voidType === null ||
      !this.scheduledOrder ||
      !this.site ||
      !accessToken
    ) {
      return;
    }

    const request: ProcessingCycleVoidRequest = {
      VoidType: this.voidType,
      Notes: this.notes,
    };

    this.isLoading = true;

    this.qpilotService
      .voidProcessingCycle(
        this.site.Id,
        this.scheduledOrder.Id,
        request,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.ref.close(request);
        },
        error: (error) => {
          console.error("Failed to void cycle:", error);
          this.isLoading = false;
          this.showError("Failed to void processing cycle");
        },
      });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
